import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { showSnack } from '@alphapoint/apex-web/lib/redux/actions/snackbarActions';
import { useTranslation } from 'react-i18next';

import InstrumentRowContainer from '@alphapoint/apex-web/lib/components/InstrumentRow';
import PriceChart from '@alphapoint/apex-web/lib/components/PriceChart/PriceChartContainer';
import DepthChart from '@alphapoint/apex-web/lib/components/DepthChart/DepthChartContainer';
import OrderHistoryComponent from '@alphapoint/apex-web/lib/components/OrderHistoryComponents';
import MarginOrderHistoryComponent from '@alphapoint/apex-web/lib/components/OrderHistoryComponents/MarginOrderHistory';
import FuturesOrderHistoryComponent from '@alphapoint/apex-web/lib/components/OrderHistoryComponents/FuturesOrderHistory';
import OrderBook from '@alphapoint/apex-web/lib/components/OrderBookComponents/OrderBookContainer';
import RecentTrades from '@alphapoint/apex-web/lib/components/RecentTrades/RecentTradesContainer';
import OrderEntry from '@alphapoint/apex-web/lib/components/OrderEntryComponents/defaultForm/OrderEntryContainer';
import FuturesOrderEntry from '@alphapoint/apex-web/lib/components/OrderEntryComponents/futures/FuturesOrderEntryForm/FuturesOrderEntryFormContainer';
import FuturesOrderBook from '@alphapoint/apex-web/lib/components/FuturesOrderBook/FuturesOrderBookContainer';
import FuturesRecentTrades from '@alphapoint/apex-web/lib/components/FuturesRecentTrades/FuturesRecentTradesContainer';
import FuturesDepthChart from '@alphapoint/apex-web/lib/components/DepthChart/FuturesDepthChartContainer';

import InstrumentPositionsWrapperContainer from '@alphapoint/apex-web/lib/components/InstrumentPositionsWrapper';
import { getBEMClasses } from '@alphapoint/apex-web/lib/helpers/cssClassesHelper';
import {
  isMarginActiveSelector,
  accountsSelector,
} from '@alphapoint/apex-web/lib/redux/margin/selectors/marginSelectors';
import { isFuturesActiveSelector } from '@alphapoint/apex-web/lib/redux/futures/selectors/futuresSelectors';
import { initSelectedInstrument } from '@alphapoint/apex-web/lib/redux/actions/instrumentActions';
import {
  futuresInstrumentsSelector,
  futuresSelectedInstrumentIdSelector,
} from '@alphapoint/apex-web/lib/redux/futures/selectors/futuresInstrumentsSelector';
import { isFuturesAccount, selectAccount } from '@alphapoint/apex-web/lib/redux/actions/userActions';
import {
  instrumentSelectorEnabledInstruments,
  selectedInstrumentIdSelector,
} from '@alphapoint/apex-web/lib/redux/selectors/instrumentPositionSelectors';
import { instrumentSelectorMarginInstruments } from '@alphapoint/apex-web/lib/redux/margin/selectors/marginInstrumentSelectors';

import config from '../../config';

import './TradingPage.css';
import { useInstrumentUrlSync } from '@alphapoint/apex-web/lib/components/InstrumentSelector/hooks/useInstrumentUrlSync';

const cls = getBEMClasses('exchange');

function getAccountTypeFromPath(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'exchange') {
    return null;
  }

  const accountSegment = segments[1];

  switch ((accountSegment || '').toLowerCase()) {
    case 'spot':
      return 'Asset';
    case 'margin':
      return 'Margin';
    case 'futures':
      return 'Futures';
    default:
      return 'Asset';
  }
}

const Tabs = ({ items, className, name }) => {
  const [activeTab, setActiveTab] = useState(items[0].id);

  return (
    <div className={`${className} ${cls('tabs')}`}>
      {items.map((item) => (
        <label key={item.id}>
          {item.title}
          <input
            type="radio"
            name={name}
            aria-controls={item.id}
            checked={activeTab === item.id}
            onClick={() => setActiveTab(item.id)}
          />
        </label>
      ))}
    </div>
  );
};

Tabs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      /** id of the element a tab controls */
      id: PropTypes.string.isRequired,
      /** title of the tab */
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
  className: PropTypes.string.isRequired,
  /** name of the tabs group */
  name: PropTypes.string.isRequired,
};

const {
  OrderHistoryData: { usePagination },
  TradingLayout: { maxLines },
} = config;

const TradingLayout = ({
  isMarginActive,
  isFuturesActive,
  selectedInstrumentId,
  instruments,
  initSelectedInstrument,
  showSnack,
}) => {
  const history = useHistory();
  const { t } = useTranslation();

  useEffect(() => {
    const tradingServer = window.localStorage.getItem('tradingServer');
    const gatewayConfig = config.global.gateway;
    const requiresServerSelection = Array.isArray(gatewayConfig);

    if (requiresServerSelection && !tradingServer) {
      const message = t('Must select a trading server');
      showSnack({
        id: 'MissingTradingServer',
        text: message,
        type: 'warning',
      });
      history.replace('/login');
    }
  }, [history, showSnack, t]);

  const location = useLocation();

  const routePrefix = location.pathname.startsWith('/exchange/margin')
    ? '/exchange/margin'
    : location.pathname.startsWith('/exchange/futures')
      ? '/exchange/futures'
      : location.pathname.startsWith('/exchange/spot')
        ? '/exchange/spot'
        : '/exchange';

  useInstrumentUrlSync({
    instruments,
    selectedInstrumentId,
    initSelectedInstrument,
    routePrefix,
  });

  const orderHistoryConfig = {
    usePagination,
    maxLines,
    filterMode: 'selectedInstrument',
  };

  const dispatch = useDispatch();
  const accounts = useSelector(accountsSelector);
  const selectedAccountId = useSelector((state) => state.user.selectedAccountId);
  const selectedAccount = accounts.find((a) => a.AccountId === selectedAccountId);
  const selectedAccountType = selectedAccount?.AccountType;

  useEffect(() => {
    const type = getAccountTypeFromPath(location.pathname);
    if (!type || type === selectedAccountType) return;
    // Find first account of this type
    const account = accounts.find((a) => a.AccountType === type);
    if (account) {
      dispatch(selectAccount(account.AccountId, account.OMSID));
    }
  }, [location.pathname, accounts, dispatch, selectedAccountType]);

  return (
    <div className={cls('container')}>
      <div className={cls('instrument-row')}>
        <InstrumentRowContainer isFuturesActive={isFuturesActive} />
      </div>
      <div className={cls('content')}>
        <div className={cls('charts-area')}>
          <Tabs
            items={[
              { id: 'price-chart', title: 'Price Chart' },
              { id: 'depth-chart', title: 'Depth Chart' },
            ]}
            name="charts"
            className={cls('charts-tabs')}
          />
          <div id="price-chart">
            <PriceChart />
          </div>
          <div id="depth-chart">{isFuturesActive ? <FuturesDepthChart smallScreen /> : <DepthChart smallScreen />}</div>
        </div>

        <div className={cls('order-book-area')}>
          <Tabs
            items={[
              { id: 'order-book', title: 'Order Book' },
              { id: 'recent-trades', title: 'Recent Trades' },
            ]}
            name="order-book"
            className={cls('order-book-tabs')}
          />
          <div id="order-book">
            <div className="trade-component__header">Order Book</div>
            {isFuturesActive ? <FuturesOrderBook /> : <OrderBook />}
          </div>
          <div id="recent-trades">
            <div className="trade-component__header">Recent Trades</div>
            {isFuturesActive ? <FuturesRecentTrades /> : <RecentTrades />}
          </div>
        </div>
        <div className={cls('order-entry-area')}>
          <Tabs
            items={[
              { id: 'order-entry', title: 'Order Entry' },
              { id: 'balances', title: 'Balances' },
            ]}
            name="order-entry"
            className={cls('order-entry-tabs')}
          />
          <div id="order-entry">
            <div className="trade-component__header">Order Entry</div>
            {isFuturesActive ? <FuturesOrderEntry /> : <OrderEntry />}
          </div>
          <div id="balances">
            <InstrumentPositionsWrapperContainer />
          </div>
        </div>

        <div className={cls('orders-area')}>
          {isFuturesActive ? (
            <FuturesOrderHistoryComponent config={orderHistoryConfig} />
          ) : isMarginActive ? (
            <MarginOrderHistoryComponent config={orderHistoryConfig} />
          ) : (
            <OrderHistoryComponent config={orderHistoryConfig} />
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const isFutures = isFuturesAccount(state);
  const isMarginActive = isMarginActiveSelector(state);
  const selectedInstrumentId = isFutures
    ? futuresSelectedInstrumentIdSelector(state)
    : selectedInstrumentIdSelector(state);
  const instruments = isFutures
    ? futuresInstrumentsSelector(state)
    : isMarginActive
      ? instrumentSelectorMarginInstruments(state)
      : instrumentSelectorEnabledInstruments(state);

  return {
    isMarginActive: isMarginActive,
    isFuturesActive: isFuturesActiveSelector(state),
    selectedInstrumentId,
    instruments,
  };
};

const mapDispatchToProps = {
  initSelectedInstrument,
  showSnack,
};

export const TradingPage = connect(mapStateToProps, mapDispatchToProps)(TradingLayout);
